/* eslint-disable react/prefer-stateless-function */
import gql from 'graphql-tag';
import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useInView } from 'react-intersection-observer';
import get from 'lodash/get';

import Text from '@redbubble/design-system/react/Text';
import Carousel, { Slide } from '@redbubble/design-system/react/Carousel';
import Box from '@redbubble/design-system/react/Box';

import ArtistCard from '../ArtistCard';
import styles from './ArtistRecommendations.css';
import LoadingSlides from '../LoadingSlides';
import { analyticsPayload } from '../../lib/analytics';
import FirstInView from '../FirstInView';
import messages from './messages';
import useCarouselImpressions from '../../hooks/useCarouselImpressions';

const ArtistRecommendations = ({
  loading,
  profile,
  onContentShowed,
  transparentCards,
  analyticsLabel,
  urlRef,
  artistRecommendations,
  browser,
  addImpression,
  userInfo
}) => {
  const recommendations = artistRecommendations || [];
  const isLoggedIn = get(userInfo, 'isLoggedIn', false);

  const logImpression = ({ recommendedArtist }, index) => {
    const artistId = get(recommendedArtist, 'artistId');
    const impression = analyticsPayload.impression(
      'explore-artist-recommendations',
      artistId,
      index + 1
    );
    addImpression(impression);
  };

  const [ref, inView] = useInView();
  const reportOnVisibility = useCarouselImpressions({
    items: recommendations,
    rows: 1,
    logImpression,
    carouselInView: inView,
    loading
  });

  const hasRecommendations = recommendations.length > 0;

  if (!loading && !hasRecommendations) return null;

  const carouselConfig = { slidesPerView: 'auto', profile };

  return (
    <Box marginBottom={2}>
      <div className={styles.header}>
        <Text type="display2" element="h2" className={styles.headerText}>
          <FormattedMessage {...messages.ArtistRecommendationHeading} />
        </Text>
      </div>
      <FirstInView key="content-showed" onFirstView={onContentShowed} />
      <div ref={ref}>
        {loading ? (
          <Carousel
            key="artist_loading_carousel"
            showControls={false}
            {...carouselConfig}
          >
            {LoadingSlides({
              numberOfItems: browser.is.large ? 6 : browser.is.medium ? 3 : 2,
              background: 'light',
              itemClass: styles.loadingGrid,
              slideClass: styles.slides
            })}
          </Carousel>
        ) : (
          <Carousel
            key="artist_carousel"
            reportOnVisibility={reportOnVisibility}
            {...carouselConfig}
          >
            {recommendations.map(
              ({ recommendedArtist, recommendedWork }, index) => (
                <Slide
                  key={`artist-${recommendedArtist.id}`}
                  className={styles.slides}
                >
                  <ArtistCard
                    analyticsValue={index}
                    analyticsLabel={analyticsLabel}
                    isLoggedIn={isLoggedIn}
                    artist={recommendedArtist}
                    work={recommendedWork}
                    hideShopButton
                    transparent={transparentCards}
                    urlRef={urlRef}
                  />
                </Slide>
              )
            )}
          </Carousel>
        )}
      </div>
    </Box>
  );
};

ArtistRecommendations.propTypes = {
  browser: PropTypes.shape({}),
  artistRecommendations: PropTypes.arrayOf(
    PropTypes.shape(ArtistCard.propsTypes)
  ),
  analyticsLabel: PropTypes.string,
  onContentShowed: PropTypes.func,
  transparentCards: PropTypes.bool,
  urlRef: PropTypes.string,
  addImpression: PropTypes.func,
  userInfo: PropTypes.shape({})
};

ArtistRecommendations.defaultProps = {
  artistRecommendations: [],
  onContentShowed: () => {},
  transparentCards: false,
  urlRef: null,
  addImpression: () => {},
  userInfo: {}
};

ArtistRecommendations.fragment = gql`
  fragment ArtistRecommendations on guru_ArtistRecommendation {
    recommendedArtist {
      artistId
    }
    ...${ArtistCard.displayName}
  }

  ${ArtistCard.fragment}
`;

ArtistRecommendations.displayName = 'ArtistRecommendations';

export default ArtistRecommendations;
